import { useState } from 'react';
import { LegacySmallCopyIcon } from './legacy-icons';

/**
 * The ID cell: `#<display_id>` with a copy affordance that appears on hover.
 *
 * The click must NOT open the order drawer — the row-level click handler would
 * otherwise fire and navigate away the moment the merchant copies an id — so
 * the button stops propagation. (The original achieved this by attaching a
 * capture-phase listener to the AG Grid cell and excluding one column index by
 * number, which broke whenever the column order changed; scoping the handler
 * to the button itself is the same behavior without that fragility.)
 */
export function LegacyIdCell({
  displayId,
  onCopied,
}: {
  displayId: number;
  onCopied: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <span
      className="inline-flex items-center gap-1.5"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="text-[16px] font-medium text-[var(--lg-grey-600)]">
        #{displayId}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          void navigator.clipboard.writeText(String(displayId));
          onCopied();
        }}
        aria-label={`${displayId}`}
        className={[
          'cursor-pointer text-[var(--lg-grey-500)] transition-opacity',
          'hover:text-[var(--lg-grey-700)] focus-visible:opacity-100',
          hovered ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
      >
        <LegacySmallCopyIcon />
      </button>
    </span>
  );
}
